import React from 'react';
import { compose } from 'redux';
import { isAdvancedCustomizationEnabled } from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { lazyForPaint } from 'react-loosely-lazy';
import type { Route } from 'react-resource-router';
import { NotFoundPage } from 'view/app/routes';
import { HelpCenter } from 'view/help-center';
import { SplashLayoutRoute } from 'view/layout';
import type { PaperAppearance } from 'view/layout/paper';
import { authPageMessage, callToActionUrlText } from 'view/portal-reorder-experience';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { withAnalyticsContext } from '@atlassian/analytics-web-react';
import { ScreenName } from '@atlassian/help-center-common-component/constants';
import { withAsync } from '@atlassian/help-center-common-component/with-async';
import { withOnlyJiraAdmin } from '@atlassian/help-center-common-component/with-only-jira-admin';
import { withOnlyLoggedIn } from '@atlassian/help-center-common-component/with-only-logged-in';
import { createRouteWithLayout } from '@atlassian/help-center-common-util/resource-router';
import EditHomepageResource from './resources/edit-homepage';
import { brandingResource, featuredAndSortedPortalsResource } from './resources/multi-help-centers';
import ViewHomepageResource from './resources/view-homepage';

const VIEW_HOMEPAGE_PATH = '/';
const EDIT_HOMEPAGE_PATH = '/edit';

const AsyncViewHomepage = withAsync({
    loader: lazyForPaint(
        () => import(/* webpackChunkName: "async-view-homepage" */ '../../view/advanced-help-center/view-homepage')
    ),
});

const paperAppearance: PaperAppearance = 'none';

const MultiHelpCenterRoute = createRouteWithLayout(
    {
        name: 'help-center',
        path: '/',
        exact: true,
        component: HelpCenter,
        resources: __SERVER__ ? [brandingResource, featuredAndSortedPortalsResource] : [],
    },
    ScreenName.HELP_CENTER,
    {
        paperAppearance: 'none',
        hideSearchButton: true,
        fadeInSearchModal: false,
    }
);

export const viewHomepageRoute = isAdvancedCustomizationEnabled()
    ? createRouteWithLayout(
          {
              name: 'view-homepage',
              path: VIEW_HOMEPAGE_PATH,
              exact: true,
              component: AsyncViewHomepage,
              resources: [ViewHomepageResource],
          },
          ScreenName.VIEW_HOMEPAGE,
          {
              paperAppearance,
              fadeInSearchModal: true,
              hideSearchButton: true,
              hideBanner: true,
          }
      )
    : MultiHelpCenterRoute;

interface Path {
    location: {
        pathname: string;
    };
}

export const AsyncEditHomepage = withAsync({
    loader: lazyForPaint(
        () => import(/* webpackChunkName: "async-edit-homepage" */ '../../view/advanced-help-center/edit-homepage')
    ),
});

export const EditHomePageLayout = (props: Path) => {
    const { formatMessage } = useIntl();
    const WrappedComponent = compose(
        withOnlyLoggedIn({
            redirectUrl: props.location.pathname,
        }),
        withOnlyJiraAdmin({
            message: formatMessage(authPageMessage),
            callToActionText: formatMessage(callToActionUrlText),
            callToActionUrl: '/',
        })
    )(() => (
        <SplashLayoutRoute
            path={EDIT_HOMEPAGE_PATH}
            component={AsyncEditHomepage}
            screenName={ScreenName.EDIT_HOMEPAGE}
        />
    ));

    return <WrappedComponent {...props} />;
};

export const editHomepageRoute: Route = {
    name: 'edit-homepage',
    path: '/edit',
    exact: true,
    component: isAdvancedCustomizationEnabled()
        ? React.memo(EditHomePageLayout)
        : withAnalyticsContext<React.ComponentProps<typeof NotFoundPage>>({ componentName: 'notFoundPage' })(
              withAnalyticsEvents({})(NotFoundPage)
          ),
    resources: [EditHomepageResource],
};
